import type { Theme } from '@aws-amplify/ui-react';
import type { ColorTokens, ModeTokens } from './tokens';
import { fontFamily, fontSize, fontWeight, radius, space } from './tokens';

/* Maps semantic tokens into Amplify's theme shape. Amplify's CSS custom
   properties auto-generate from this tree; components that use Amplify
   tokens ('colors.background.primary', etc.) read the correct value per
   color mode because the overrides[colorMode] block replaces the base. */

function colorsBlock(c: ColorTokens) {
  return {
    background: {
      primary: { value: c.surface.base },
      secondary: { value: c.surface.raised },
      tertiary: { value: c.surface.sunken },
      success: { value: c.status.success.bg },
      warning: { value: c.status.warning.bg },
      error: { value: c.status.danger.bg },
      info: { value: c.status.info.bg },
    },
    // Exposes --amplify-colors-status-{kind}-{bg,fg} so StatusPill and the
    // danger/warning surfaces get the per-mode (dark-tinted) values instead of
    // their light-mode CSS fallbacks.
    status: {
      success: { bg: { value: c.status.success.bg }, fg: { value: c.status.success.fg } },
      warning: { bg: { value: c.status.warning.bg }, fg: { value: c.status.warning.fg } },
      danger: { bg: { value: c.status.danger.bg }, fg: { value: c.status.danger.fg } },
      info: { bg: { value: c.status.info.bg }, fg: { value: c.status.info.fg } },
    },
    font: {
      primary: { value: c.text.primary },
      secondary: { value: c.text.secondary },
      tertiary: { value: c.text.muted },
      inverse: { value: c.text.onAccent },
      success: { value: c.status.success.fg },
      warning: { value: c.status.warning.fg },
      error: { value: c.status.danger.fg },
      info: { value: c.status.info.fg },
    },
    border: {
      primary: { value: c.border.default },
      secondary: { value: c.border.subtle },
      tertiary: { value: c.border.strong },
      focus: { value: c.border.focus },
    },
    brand: {
      primary: {
        10: { value: c.accent.soft },
        20: { value: c.accent.soft },
        40: { value: c.accent.hover },
        60: { value: c.accent.default },
        80: { value: c.accent.default },
        90: { value: c.accent.pressed },
        100: { value: c.accent.pressed },
      },
    },
    // The unscoped `colors.primary` ramp is what Amplify's form controls
    // (checkbox icon, switchfield track, radio, togglebutton pressed) read by
    // default — they bypass `brand.primary`. Mapping it to the same Stone Rose
    // accent ramp keeps every checked/active form state on-brand instead of
    // falling back to Amplify's default teal blue.
    primary: {
      10: { value: c.accent.soft },
      20: { value: c.accent.soft },
      40: { value: c.accent.hover },
      60: { value: c.accent.default },
      80: { value: c.accent.default },
      90: { value: c.accent.pressed },
      100: { value: c.accent.pressed },
    },
    overlay: {
      10: { value: c.surface.overlay },
      20: { value: c.surface.overlay },
      40: { value: c.surface.overlay },
      60: { value: c.surface.overlay },
    },
  };
}

function componentsBlock(c: ColorTokens) {
  return {
    button: {
      // Default (neutral) variation: warm muted disabled text, not Amplify's cool grey.
      _disabled: {
        color: { value: c.text.muted },
        borderColor: { value: c.border.subtle },
      },
      primary: {
        backgroundColor: { value: c.accent.default },
        color: { value: c.text.onAccent },
        borderColor: { value: c.accent.default },
        _hover: {
          backgroundColor: { value: c.accent.hover },
          borderColor: { value: c.accent.hover },
          color: { value: c.text.onAccent },
        },
        _focus: {
          backgroundColor: { value: c.accent.hover },
          borderColor: { value: c.border.focus },
          color: { value: c.text.onAccent },
          boxShadow: { value: `0 0 0 2px ${c.border.focus}` },
        },
        _active: {
          backgroundColor: { value: c.accent.pressed },
          borderColor: { value: c.accent.pressed },
        },
        _disabled: {
          backgroundColor: { value: c.surface.sunken },
          color: { value: c.text.muted },
          borderColor: { value: c.border.subtle },
        },
      },
      link: {
        color: { value: c.accent.default },
        _hover: { color: { value: c.accent.hover }, backgroundColor: { value: c.accent.soft } },
        _focus: {
          color: { value: c.accent.hover },
          boxShadow: { value: `0 0 0 2px ${c.border.focus}` },
        },
        _active: { color: { value: c.accent.pressed } },
        _disabled: {
          color: { value: c.text.muted },
          backgroundColor: { value: 'transparent' },
        },
      },
      outlined: {
        color: { value: c.text.primary },
        borderColor: { value: c.border.default },
        _disabled: {
          color: { value: c.text.muted },
          borderColor: { value: c.border.subtle },
          backgroundColor: { value: 'transparent' },
        },
        _hover: {
          backgroundColor: { value: c.accent.soft },
          borderColor: { value: c.accent.default },
          color: { value: c.text.primary },
        },
        _focus: {
          borderColor: { value: c.border.focus },
          color: { value: c.text.primary },
          boxShadow: { value: `0 0 0 2px ${c.border.focus}` },
        },
        _active: {
          backgroundColor: { value: c.accent.soft },
          borderColor: { value: c.accent.pressed },
        },
      },
      warning: {
        backgroundColor: { value: c.status.warning.bg },
        color: { value: c.status.warning.fg },
        borderColor: { value: c.status.warning.bg },
      },
      destructive: {
        backgroundColor: { value: c.status.danger.bg },
        color: { value: c.status.danger.fg },
        borderColor: { value: c.status.danger.bg },
        _hover: {
          backgroundColor: { value: c.status.danger.fg },
          color: { value: c.text.onDanger },
        },
      },
      // The Menu dropdown renders each MenuItem as <Button variation="menu">.
      // Without this override Amplify falls back to its default `colors.primary.80`
      // ramp (a teal blue), which collides with the Geisti Stone Rose accent.
      menu: {
        backgroundColor: { value: 'transparent' },
        color: { value: c.text.primary },
        _hover: {
          backgroundColor: { value: c.accent.soft },
          color: { value: c.text.primary },
        },
        _focus: {
          backgroundColor: { value: c.accent.soft },
          color: { value: c.text.primary },
        },
        _active: {
          backgroundColor: { value: c.accent.default },
          color: { value: c.text.onAccent },
        },
        _disabled: {
          color: { value: c.text.muted },
        },
      },
    },
    card: {
      backgroundColor: { value: c.surface.raised },
      borderColor: { value: c.border.subtle },
      boxShadow: { value: '0 1px 2px rgba(0,0,0,0.04)' },
    },
    fieldcontrol: {
      borderColor: { value: c.border.default },
      color: { value: c.text.primary },
      _focus: {
        borderColor: { value: c.border.focus },
        boxShadow: { value: `0 0 0 2px ${c.border.focus}` },
      },
      _disabled: {
        backgroundColor: { value: c.surface.sunken },
        color: { value: c.text.muted },
      },
    },
    tabs: {
      borderColor: { value: c.border.subtle },
      item: {
        color: { value: c.text.secondary },
        borderColor: { value: c.border.subtle },
        _hover: { color: { value: c.text.primary } },
        _focus: {
          color: { value: c.text.primary },
          boxShadow: { value: `0 0 0 2px ${c.border.focus}` },
        },
        _active: { color: { value: c.accent.default }, borderColor: { value: c.accent.default } },
      },
    },
    badge: {
      info: { backgroundColor: { value: c.status.info.bg }, color: { value: c.status.info.fg } },
      success: {
        backgroundColor: { value: c.status.success.bg },
        color: { value: c.status.success.fg },
      },
      warning: {
        backgroundColor: { value: c.status.warning.bg },
        color: { value: c.status.warning.fg },
      },
      error: {
        backgroundColor: { value: c.status.danger.bg },
        color: { value: c.status.danger.fg },
      },
    },
    pagination: {
      current: {
        backgroundColor: { value: c.accent.soft },
        color: { value: c.accent.default },
      },
    },
    breadcrumbs: {
      separator: { color: { value: c.text.muted } },
      link: {
        color: { value: c.text.secondary },
        current: { color: { value: c.text.primary } },
      },
    },
    dropzone: {
      backgroundColor: { value: c.surface.raised },
      borderColor: { value: c.border.default },
      _active: {
        backgroundColor: { value: c.accent.soft },
        borderColor: { value: c.accent.default },
      },
      rejected: {
        backgroundColor: { value: c.status.danger.bg },
        color: { value: c.status.danger.fg },
      },
    },
    loader: {
      strokeFilled: { value: c.accent.default },
      strokeEmpty: { value: c.border.subtle },
    },
    alert: {
      info: { backgroundColor: { value: c.status.info.bg }, color: { value: c.status.info.fg } },
      success: {
        backgroundColor: { value: c.status.success.bg },
        color: { value: c.status.success.fg },
      },
      warning: {
        backgroundColor: { value: c.status.warning.bg },
        color: { value: c.status.warning.fg },
      },
      error: {
        backgroundColor: { value: c.status.danger.bg },
        color: { value: c.status.danger.fg },
      },
    },
    menu: {
      backgroundColor: { value: c.surface.raised },
      borderColor: { value: c.border.subtle },
      item: {
        color: { value: c.text.primary },
        _hover: { backgroundColor: { value: c.accent.soft } },
        _focus: { backgroundColor: { value: c.accent.soft }, color: { value: c.text.primary } },
      },
    },
    placeholder: {
      startColor: { value: c.surface.sunken },
      endColor: { value: c.surface.raised },
    },
  };
}

export default function buildAmplifyTheme(preset: ModeTokens): Theme {
  const lightColors = colorsBlock(preset.light);
  const lightComponents = componentsBlock(preset.light);
  const darkColors = colorsBlock(preset.dark);
  const darkComponents = componentsBlock(preset.dark);

  return {
    name: 'geisti-theme',
    tokens: {
      colors: lightColors,
      fonts: {
        default: {
          variable: { value: fontFamily.sans },
          static: { value: fontFamily.sans },
        },
      },
      fontSizes: {
        xxs: { value: fontSize.xs },
        xs: { value: fontSize.xs },
        small: { value: fontSize.sm },
        medium: { value: fontSize.base },
        large: { value: fontSize.lg },
        xl: { value: fontSize.xl },
        xxl: { value: fontSize['2xl'] },
        xxxl: { value: fontSize['3xl'] },
      },
      fontWeights: {
        normal: { value: fontWeight.regular },
        medium: { value: fontWeight.medium },
        semibold: { value: fontWeight.semibold },
        bold: { value: fontWeight.semibold },
      },
      radii: {
        xs: { value: radius.sm },
        small: { value: radius.sm },
        medium: { value: radius.md },
        large: { value: radius.lg },
        xl: { value: radius.lg },
      },
      space: {
        xs: { value: space[1] },
        small: { value: space[2] },
        medium: { value: space[4] },
        large: { value: space[6] },
        xl: { value: space[8] },
        xxl: { value: space[10] },
        xxxl: { value: space[12] },
      },
      components: lightComponents,
      // Amplify's public Theme type uses a narrower shape; we pass the broader
      // PartialDeep<Tokens> form accepted at runtime. Cast is intentional and isolated to this function.
    } as unknown as Theme['tokens'],
    overrides: [
      {
        colorMode: 'dark',
        tokens: {
          colors: darkColors,
          components: darkComponents,
        },
      },
      // Same cast rationale: overrides accept PartialDeep at runtime but the public type is stricter.
    ] as unknown as Theme['overrides'],
  };
}
